import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPublicJivoConfig } from '@/api/publicJivo';

/** Глобальный объект виджета Jivo (создаётся скриптом code.jivo.ru; переживает unmount). */
declare global {
  interface Window {
    jivo?: unknown;
  }
}

/** widgetId последнего проинжекченного скрипта (модульное состояние — переживает unmount). */
let injectedWidgetId: string | null = null;

/**
 * Формат widgetId (pintest S4 M1 defense-in-depth, паритет с BE @Pattern
 * JivoSettingsUpdateRequest): мусорный/подменённый ID не инжектится в src
 * {@code //code.jivo.ru/widget/<id>} — максимум «битый виджет» исключён.
 */
const WIDGET_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** Сброс guard-состояния между тестами (только для тестов: модульное состояние переживает тесты). */
export function resetJivoInjectionStateForTests(): void {
  injectedWidgetId = null;
}

/**
 * Подключает скрипт виджета Jivo на лендинге по runtime-конфигу BE.
 * Не рендерим: пока грузится, при ошибке, при enabled=false, при пустом widgetId.
 * Повторный инжект того же widgetId при живом window.jivo подавляется (инжекты
 * самого Jivo переживают unmount — повторное исполнение скрипта дублировало бы
 * виджет); смена widgetId → повторный инжект (перезагрузка виджета корректна).
 */
export function JivoWidget(): null {
  const { data, isSuccess } = useQuery({
    queryKey: ['public', 'jivo-config'],
    queryFn: fetchPublicJivoConfig,
    retry: 1,
    // Не HTTP-кэш, а client-side дедуп в рамках сессии (не противоречит R-65).
    staleTime: 5 * 60 * 1000,
  });
  const widgetId =
    isSuccess && data.enabled && data.widgetId !== null && WIDGET_ID_PATTERN.test(data.widgetId)
      ? data.widgetId
      : null;

  useEffect(() => {
    if (!widgetId) {
      return undefined;
    }
    if (injectedWidgetId === widgetId && typeof window.jivo !== 'undefined') {
      return undefined;
    }
    const script = document.createElement('script');
    script.src = `//code.jivo.ru/widget/${widgetId}`;
    script.async = true;
    script.dataset.jivoWidget = 'true';
    document.head.appendChild(script);
    injectedWidgetId = widgetId;
    return () => {
      script.remove();
    };
  }, [widgetId]);
  return null;
}
